/**
 * Alert Detail Panel
 *
 * Slide-out panel showing full alert details when clicking an alert row.
 */

import React from 'react';
import {
  X,
  AlertCircle,
  User,
  Shield,
  Clock,
  CheckCircle,
  ArrowUp,
  ExternalLink,
  FolderKanban,
  Eye,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { formatListSource } from '../utils/formatters';
import { Button, Badge } from './ui';

// Severity config with Badge variants
const severityConfig = {
  critical: { variant: 'destructive', label: 'Critical' },
  high: { variant: 'warning', label: 'High' },
  medium: { variant: 'secondary', label: 'Medium' },
  low: { variant: 'success', label: 'Low' },
};

// Category labels
const categoryLabels = {
  sanctions: 'Sanctions',
  pep: 'PEP',
  adverse_media: 'Adverse Media',
  wallet_screening: 'Wallet Screening',
  transaction_monitoring: 'Transaction Monitoring',
  market_surveillance: 'Market Surveillance',
  screening: 'Screening',
};

// Status config
const statusConfig = {
  open: { variant: 'info', label: 'Open' },
  reviewing: { variant: 'secondary', label: 'Reviewing' },
  resolved: { variant: 'success', label: 'Resolved' },
  dismissed: { variant: 'outline', label: 'Dismissed' },
  escalated: { variant: 'warning', label: 'Escalated' },
};

export default function AlertDetailPanel({
  alert,
  onClose,
  onResolve,
  onEscalate,
  onCreateCase,
  isCreatingCase = false,
}) {
  if (!alert) return null;

  const severity = severityConfig[alert.severity] || severityConfig.medium;
  const status = statusConfig[alert.status] || statusConfig.open;
  const isActionable = !['resolved', 'dismissed'].includes(alert.status);

  return (
    <div
      className="fixed inset-0 bg-black/30 z-[500]"
      onClick={onClose}
    >
      <div
        className="fixed right-0 top-0 bottom-0 w-[480px] max-w-[90vw] bg-white dark:bg-grey-900 border-l border-grey-200 dark:border-grey-700 flex flex-col animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-grey-200 dark:border-grey-700">
          <h2 className="text-base font-semibold">Alert Details</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X size={18} />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Alert Info */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-grey-500 mb-3">
              Alert Information
            </h3>
            <div className="grid gap-3">
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-grey-500">Category</span>
                <span className="text-[13px] font-medium flex items-center gap-1">
                  <Shield size={14} />
                  {categoryLabels[alert.category] || alert.category}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-grey-500">Severity</span>
                <Badge variant={severity.variant} className="flex items-center gap-1.5">
                  <AlertCircle size={12} />
                  {severity.label}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-grey-500">Status</span>
                <Badge variant={status.variant} className="flex items-center gap-1">
                  {alert.status === 'open' && <Clock size={14} />}
                  {alert.status === 'resolved' && <CheckCircle size={14} />}
                  {alert.status === 'escalated' && <ArrowUp size={14} />}
                  {status.label}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-grey-500">Created</span>
                <span className="text-[13px] font-medium">
                  {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[13px] text-grey-500">Alert ID</span>
                <span className="text-[11px] font-mono font-medium">
                  {alert.id}
                </span>
              </div>
            </div>
          </div>

          {/* Hit Information */}
          {alert.new_hits && alert.new_hits.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-grey-500 mb-3">
                Screening Hits ({alert.hit_count || alert.new_hits.length})
              </h3>
              {alert.new_hits.map((hit, idx) => (
                <div
                  key={idx}
                  className="bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg p-3 mb-2 last:mb-0"
                >
                  <div className="text-sm font-semibold mb-2">
                    {hit.matched_name || 'Unknown Match'}
                  </div>
                  <div className="text-xs text-grey-500 flex gap-3 flex-wrap">
                    {hit.confidence && (
                      <span className="flex items-center gap-1">
                        <strong>{hit.confidence}%</strong> confidence
                      </span>
                    )}
                    {hit.list_source && (
                      <span>List: {formatListSource(hit.list_source)}</span>
                    )}
                    {hit.hit_type && (
                      <span>Type: {hit.hit_type}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Applicant */}
          {alert.applicant && (
            <div className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-grey-500 mb-3">
                Applicant
              </h3>
              <div className="bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-lg p-4">
                <div className="text-base font-semibold flex items-center gap-2 mb-2">
                  <User size={16} />
                  {alert.applicant.full_name || 'Unknown'}
                </div>
                <div className="text-[13px] text-grey-500">
                  {alert.applicant.email && <div>Email: {alert.applicant.email}</div>}
                  {alert.applicant.nationality && <div>Nationality: {alert.applicant.nationality}</div>}
                </div>
                <a
                  href={`/applicants/${alert.applicant_id}`}
                  className="inline-flex items-center gap-1 text-[13px] text-teal-600 dark:text-teal-400 hover:underline mt-3"
                >
                  <Eye size={14} />
                  View Full Profile
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>
          )}

          {/* Resolution (if resolved) */}
          {(alert.status === 'resolved' || alert.status === 'dismissed') && (
            <div className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-grey-500 mb-3">
                Resolution
              </h3>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <div className="text-sm font-semibold text-green-600 dark:text-green-400 mb-1.5">
                  {alert.resolution_action?.replace(/_/g, ' ').toUpperCase() || 'Resolved'}
                </div>
                {alert.resolution_notes && (
                  <div className="text-[13px] text-grey-600 dark:text-grey-400">
                    {alert.resolution_notes}
                  </div>
                )}
                {alert.resolved_at && (
                  <div className="text-[13px] text-grey-500 mt-2">
                    Resolved {formatDistanceToNow(new Date(alert.resolved_at), { addSuffix: true })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Escalation (if escalated) */}
          {alert.status === 'escalated' && (
            <div className="mb-6">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-grey-500 mb-3">
                Escalation
              </h3>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                <div className="text-sm font-semibold text-orange-600 dark:text-orange-400 mb-1.5">
                  Escalated to CCO
                </div>
                {alert.escalation_reason && (
                  <div className="text-[13px] text-grey-600 dark:text-grey-400">
                    {alert.escalation_reason}
                  </div>
                )}
                {alert.escalated_at && (
                  <div className="text-[13px] text-grey-500 mt-2">
                    Escalated {formatDistanceToNow(new Date(alert.escalated_at), { addSuffix: true })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-4 border-t border-grey-200 dark:border-grey-700 flex gap-3">
          {isActionable ? (
            <>
              <Button variant="secondary" className="flex-1" onClick={onResolve}>
                <CheckCircle size={16} className="mr-2" />
                Resolve
              </Button>
              <Button variant="secondary" className="flex-1" onClick={onEscalate}>
                <ArrowUp size={16} className="mr-2" />
                Escalate
              </Button>
              <Button
                variant="default"
                className="flex-1"
                onClick={onCreateCase}
                disabled={isCreatingCase}
              >
                <FolderKanban size={16} className="mr-2" />
                {isCreatingCase ? 'Creating...' : 'Create Case'}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => window.location.href = `/applicants/${alert.applicant_id}`}
            >
              <Eye size={16} className="mr-2" />
              View Applicant
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
